import type { OrgDirectoryAdapter, OrgMember } from '@zhiyue/core';
import { Select } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { orgDirectoryAdapter } from '@/modules/zy-crm/utils/org-directory-adapter';

type MemberPickerBaseProps = {
  adapter?: OrgDirectoryAdapter;
  token?: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
};

type MemberPickerProps = MemberPickerBaseProps & {
  value?: string | null;
  onChange?: (value: string | null) => void;
};

type MemberMultiPickerProps = MemberPickerBaseProps & {
  value?: string[];
  onChange?: (value: string[]) => void;
};

const memberLabel = (member: OrgMember) =>
  member.name || member.nickname || member.username || member.memberUuid;

const useMemberOptions = (
  adapter: OrgDirectoryAdapter,
  keyword: string,
  active: boolean,
) => {
  const [members, setMembers] = useState<OrgMember[]>([]);

  useEffect(() => {
    if (!active) return;

    let cancelled = false;
    const timer = window.setTimeout(() => {
      void adapter
        .searchMembers({ keyword, pageNo: 1, pageSize: 100 })
        .then((result) => {
          if (!cancelled) setMembers(result.list);
        })
        .catch(() => {
          if (!cancelled) setMembers([]);
        });
    }, 200);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [active, adapter, keyword]);

  return useMemo(
    () =>
      members.map((member) => ({
        label: memberLabel(member),
        value: member.memberUuid,
      })),
    [members],
  );
};

export function MemberPicker({
  adapter = orgDirectoryAdapter,
  disabled,
  placeholder = '请选择成员',
  value,
  onChange,
  className,
  style,
}: MemberPickerProps) {
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const options = useMemberOptions(adapter, keyword, open || Boolean(value));

  return (
    <Select
      allowClear
      showSearch
      className={className}
      disabled={disabled}
      filterOption={false}
      options={options}
      placeholder={placeholder}
      style={{ width: '100%', ...style }}
      value={value || undefined}
      onOpenChange={setOpen}
      onChange={(nextValue) => onChange?.(nextValue || null)}
      onSearch={setKeyword}
    />
  );
}

export function MemberMultiPicker({
  adapter = orgDirectoryAdapter,
  disabled,
  placeholder = '请选择成员',
  value,
  onChange,
  className,
  style,
}: MemberMultiPickerProps) {
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const options = useMemberOptions(
    adapter,
    keyword,
    open || Boolean(value?.length),
  );

  return (
    <Select
      allowClear
      showSearch
      mode="multiple"
      className={className}
      disabled={disabled}
      filterOption={false}
      options={options}
      placeholder={placeholder}
      style={{ width: '100%', ...style }}
      value={value || []}
      onOpenChange={setOpen}
      onChange={(nextValue) => onChange?.(nextValue)}
      onSearch={setKeyword}
    />
  );
}
